import React, { useState } from "react";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { StringParam, useQueryParam } from "use-query-params";
import fetchLoginStatus from "../../util/login-status";
import { getProductFrom } from "./subscriptionUtils";

/* ── Tokens ─────────────────────────────────────────────────── */
const C = {
    surface: "#ffffff",
    border:  "#e2e8f0",
    text:    "#0f172a",
    muted:   "#64748b",
    indigo:  "#6366f1",
    indigoD: "#4f46e5",
    green:   "#16a34a",
    greenBg: "#f0fdf4",
    blue:    "#0369a1",
    blueBg:  "#f0f9ff",
};

const Card = styled.div`
    background: ${C.surface};
    border: 1px solid ${C.border};
    border-radius: 12px;
    overflow: hidden;
    max-width: 480px;
    font-family: "Inter", system-ui, sans-serif;
`;

const CardHeader = styled.div`
    padding: 16px 20px;
    border-bottom: 1px solid ${C.border};
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const CardTitle = styled.h3`
    margin: 0;
    font-size: 0.9rem;
    font-weight: 700;
    color: ${C.text};
`;

const CardBody = styled.div`
    padding: 24px 20px;
`;

const PlanRow = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
`;

const PlanIcon = styled.div`
    width: 52px;
    height: 52px;
    border-radius: 12px;
    background: #f1f5f9;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    img { width: 28px; height: 28px; }
`;

const PlanInfo = styled.div`
    flex: 1;
`;

const PlanName = styled.div`
    font-size: 1.1rem;
    font-weight: 700;
    color: ${C.text};
    margin-bottom: 2px;
`;

const PlanPrice = styled.div`
    font-size: 0.875rem;
    color: ${C.muted};
`;

const ChangeBtn = styled.a`
    display: inline-flex;
    align-items: center;
    padding: 8px 16px;
    background: ${C.indigo};
    color: #fff;
    border: none;
    border-radius: 8px;
    font-size: 0.85rem;
    font-weight: 600;
    font-family: "Inter", system-ui, sans-serif;
    text-decoration: none;
    cursor: pointer;
    transition: background 120ms;
    flex-shrink: 0;
    &:hover { background: ${C.indigoD}; color: #fff; text-decoration: none; }
`;

const Alert = styled.div`
    margin-bottom: 20px;
    padding: 14px 16px;
    background: ${({ $type }) => $type === "success" ? C.greenBg : C.blueBg};
    border: 1px solid ${({ $type }) => $type === "success" ? "#bbf7d0" : "#bae6fd"};
    border-radius: 8px;
    font-size: 0.875rem;
    color: ${({ $type }) => $type === "success" ? C.green : C.blue};
`;

const AlertTitle = styled.div`
    font-weight: 700;
    margin-bottom: 4px;
`;

const CloseBtn = styled.button`
    float: right;
    background: none;
    border: none;
    cursor: pointer;
    color: inherit;
    opacity: 0.6;
    font-size: 1rem;
    line-height: 1;
    padding: 0;
    &:hover { opacity: 1; }
`;

/* ── Plan icon lookup ────────────────────────────────────────── */
import startIcon    from './static/start_white.svg';
import startUgly    from './static/start.svg';
import diamond      from './static/diamond.svg';
import idea         from './static/idea.svg';
import giftIcon     from './static/giftbox.svg';

const planIcons = {
    generic:  startUgly,
    free:     giftIcon,
    starter:  startIcon,
    pro:      diamond,
    diamond:  diamond,
    premium:  diamond,
    business: diamond,
    idea:     idea,
};

function getPlanIcon(product) {
    if (!product?.featuresGroup) return planIcons.generic;
    return planIcons[product.featuresGroup] || planIcons.generic;
}

/* ── Component ───────────────────────────────────────────────── */
export const SubscriptionView = () => {
    const login              = useSelector(s => s.login);
    const billingInformation = useSelector(s => s.login.userDetails.billingInformation);
    const dispatch           = useDispatch();
    const [changeResult, setChangeResult] = useQueryParam('changeResult', StringParam);
    const [refreshed, setRefreshed]       = useState(false);

    const product = getProductFrom(billingInformation);

    if (changeResult === "success" && !refreshed) {
        setTimeout(() => {
            setRefreshed(true);
            login.userDetails.fetchAttempted = false;
            dispatch(fetchLoginStatus(login));
        }, 3000);
    }

    const priceText = product.price > 0
        ? `£${(product.price / 100).toFixed(2)} / ${product.priceInterval}`
        : "Free";

    return (
        <Card>
            <CardHeader>
                <CardTitle>Your plan</CardTitle>
            </CardHeader>
            <CardBody>
                {changeResult === "success" && (
                    <Alert $type="success">
                        <CloseBtn onClick={() => setChangeResult(null)}>✕</CloseBtn>
                        <AlertTitle>Plan updated! 🎉</AlertTitle>
                        You've successfully changed your subscription. It might take a few seconds to reflect below.
                    </Alert>
                )}
                {changeResult === "cancelled" && (
                    <Alert $type="info">
                        <CloseBtn onClick={() => setChangeResult(null)}>✕</CloseBtn>
                        <AlertTitle>No changes made</AlertTitle>
                        Your subscription was not changed.
                    </Alert>
                )}

                <PlanRow>
                    <PlanIcon>
                        <img src={getPlanIcon(product)} alt={product.name} />
                    </PlanIcon>
                    <PlanInfo>
                        <PlanName>{product.name}</PlanName>
                        <PlanPrice>{priceText}</PlanPrice>
                    </PlanInfo>
                    <ChangeBtn href="/#/change-subscription">Change plan</ChangeBtn>
                </PlanRow>
            </CardBody>
        </Card>
    );
};
